import { useEffect, useRef } from 'react';
import { activeStationsApi } from '../api/logbookApi';
import { useOffline } from '../context/OfflineContext';

const CHECK_INTERVAL = 5 * 60 * 1000; // 5 perc
const PROXIMITY_RADIUS = 300; // méter

function getDistanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function checkPosition(stations, notified, onStationFound, onDebug) {
  if (!navigator.geolocation) {
    onDebug?.('GPS nem támogatott');
    return;
  }
  if (stations.length === 0) {
    onDebug?.('Nincs állomás betöltve');
    return;
  }

  onDebug?.('GPS lekérdezés...');

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { latitude, longitude } = position.coords;
      onDebug?.(`Pozíció: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);

      let minDistance = Infinity;
      let closestStation = null;

      for (const station of stations) {
        const distance = getDistanceMeters(
          latitude, longitude,
          parseFloat(station.latitude),
          parseFloat(station.longitude)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestStation = station;
        }

        if (distance <= PROXIMITY_RADIUS && !notified.has(station.id)) {
          notified.add(station.id);
          onStationFound(station, distance);
          onDebug?.(`✅ Közel: ${station.station_name} (${Math.round(distance)}m)`);
          break;
        }
      }

      if (closestStation) {
        onDebug?.(`Legközelebbi: ${closestStation.station_name} – ${Math.round(minDistance)}m`);
      }
    },
    (err) => onDebug?.(`GPS hiba: ${err.message} (kód: ${err.code})`),
    { timeout: 10000, enableHighAccuracy: false, maximumAge: 120000 }
  );
}

export default function ProximityDetector({ onStationFound, onDebug }) {
  const { isOnline } = useOffline();
  const stationsRef = useRef([]);
  const notifiedRef = useRef(new Set());
  const onStationFoundRef = useRef(onStationFound);
  const onDebugRef = useRef(onDebug);

  // Ref-eket frissítjük hogy mindig a legfrissebb callback legyen
  useEffect(() => {
    onStationFoundRef.current = onStationFound;
    onDebugRef.current = onDebug;
  });

  useEffect(() => {
    if (!isOnline) return;

    const load = async () => {
      try {
        onDebugRef.current?.('Állomások betöltése...');
        const res = await activeStationsApi.list();
        stationsRef.current = res.data.filter(s => s.latitude && s.longitude);
        onDebugRef.current?.(`${stationsRef.current.length} állomás betöltve koordinátákkal`);

        // Azonnal ellenőrzünk betöltés után!
        checkPosition(
          stationsRef.current,
          notifiedRef.current,
          onStationFoundRef.current,
          onDebugRef.current
        );
      } catch (err) {
        onDebugRef.current?.(`Állomás betöltési hiba: ${err.message}`);
      }
    };

    // Azonnal betöltés és ellenőrzés
    load();

    // 30 percenként újratöltés + ellenőrzés
    const loadInterval = setInterval(load, 30 * 60 * 1000);

    // 5 percenként ismételt ellenőrzés
    const checkInterval = setInterval(() => {
      checkPosition(
        stationsRef.current,
        notifiedRef.current,
        onStationFoundRef.current,
        onDebugRef.current
      );
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(loadInterval);
      clearInterval(checkInterval);
    };
  }, [isOnline]);

  return null;
}